import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const zodiacDescriptions = {
  'Áries': {
    element: 'Fogo',
    quality: 'Cardinal',
    ruler: 'Marte',
    description: 'Áries é o primeiro signo do zodíaco, representando novos começos, liderança e iniciativa. São pessoas naturalmente energéticas, corajosas e espontâneas.',
    strengths: ['Corajoso', 'Determinado', 'Confiante', 'Entusiasta', 'Otimista', 'Honesto'],
    weaknesses: ['Impaciente', 'Temperamental', 'Impulsivo', 'Agressivo'],
    symbol: '♈'
  },
  'Touro': {
    element: 'Terra',
    quality: 'Fixo',
    ruler: 'Vênus',
    description: 'Touro é o segundo signo do zodíaco, simbolizando estabilidade, sensualidade e materialidade. São pessoas práticas, determinadas e apreciadoras do conforto.',
    strengths: ['Paciente', 'Prático', 'Confiável', 'Dedicado', 'Estável'],
    weaknesses: ['Teimoso', 'Possessivo', 'Inflexível'],
    symbol: '♉'
  },
  'Gêmeos': {
    element: 'Ar',
    quality: 'Mutável',
    ruler: 'Mercúrio',
    description: 'Gêmeos é o terceiro signo do zodíaco, representando comunicação, versatilidade e curiosidade. São pessoas adaptáveis, inteligentes e sociáveis.',
    strengths: ['Comunicativo', 'Inteligente', 'Adaptável', 'Versátil', 'Curioso', 'Divertido'],
    weaknesses: ['Inconstante', 'Ansioso', 'Indeciso', 'Superficial'],
    symbol: '♊'
  },
  'Câncer': {
    element: 'Água',
    quality: 'Cardinal',
    ruler: 'Lua',
    description: 'Câncer é o quarto signo do zodíaco, simbolizando emoção, intuição e proteção. São pessoas sensíveis, protetoras e com forte conexão familiar.',
    strengths: ['Intuitivo', 'Protetor', 'Leal', 'Empático', 'Criativo'],
    weaknesses: ['Emotivo', 'Manipulador', 'Pessimista', 'Rancoroso'],
    symbol: '♋'
  },
  'Leão': {
    element: 'Fogo',
    quality: 'Fixo',
    ruler: 'Sol',
    description: 'Leão é o quinto signo do zodíaco, simbolizando criatividade, autoexpressão e liderança. São pessoas carismáticas, generosas e naturalmente dramáticas.',
    strengths: ['Criativo', 'Generoso', 'Carismático', 'Líder nato', 'Confiante', 'Leal'],
    weaknesses: ['Arrogante', 'Autoritário', 'Dramático', 'Teimoso'],
    symbol: '♌'
  },
  'Virgem': {
    element: 'Terra',
    quality: 'Mutável',
    ruler: 'Mercúrio',
    description: 'Virgem é o sexto signo do zodíaco, representando análise, perfeição e serviço. São pessoas meticulosas, práticas e dedicadas ao trabalho.',
    strengths: ['Analítico', 'Organizado', 'Prático', 'Dedicado', 'Confiável', 'Meticuloso'],
    weaknesses: ['Crítico', 'Perfeccionista', 'Ansioso', 'Preocupado'],
    symbol: '♍'
  },
  'Libra': {
    element: 'Ar',
    quality: 'Cardinal',
    ruler: 'Vênus',
    description: 'Libra é o sétimo signo do zodíaco, simbolizando equilíbrio, harmonia e justiça. São pessoas diplomáticas, sociáveis e apreciadoras da beleza e das artes.',
    strengths: ['Diplomático', 'Justo', 'Sociável', 'Charmoso', 'Pacífico', 'Artístico'],
    weaknesses: ['Indeciso', 'Evita conflitos', 'Superficial', 'Dependente'],
    symbol: '♎'
  },
  'Escorpião': {
    element: 'Água',
    quality: 'Fixo',
    ruler: 'Plutão',
    description: 'Escorpião é o oitavo signo do zodíaco, representando intensidade, mistério e transformação. São pessoas determinadas, perspicazes e profundamente emocionais.',
    strengths: ['Determinado', 'Intenso', 'Perspicaz', 'Leal', 'Poderoso', 'Investigativo'],
    weaknesses: ['Obsessivo', 'Ciumento', 'Vingativo', 'Manipulador'],
    symbol: '♏'
  },
  'Sagitário': {
    element: 'Fogo',
    quality: 'Mutável',
    ruler: 'Júpiter',
    description: 'Sagitário é o nono signo do zodíaco, simbolizando expansão, aventura e sabedoria. São pessoas otimistas, filosóficas e amantes da liberdade.',
    strengths: ['Otimista', 'Aventureiro', 'Honesto', 'Filosófico', 'Entusiasta', 'Independente'],
    weaknesses: ['Imprudente', 'Impaciente', 'Exagerado', 'Irresponsável'],
    symbol: '♐'
  },
  'Capricórnio': {
    element: 'Terra',
    quality: 'Cardinal',
    ruler: 'Saturno',
    description: 'Capricórnio é o décimo signo do zodíaco, representando ambição, disciplina e responsabilidade. São pessoas determinadas, práticas e muito trabalhadoras.',
    strengths: ['Disciplinado', 'Responsável', 'Ambicioso', 'Paciente', 'Determinado', 'Organizado'],
    weaknesses: ['Pessimista', 'Rígido', 'Materialista', 'Frio'],
    symbol: '♑'
  },
  'Aquário': {
    element: 'Ar',
    quality: 'Fixo',
    ruler: 'Urano',
    description: 'Aquário é o décimo primeiro signo do zodíaco, simbolizando inovação, originalidade e progresso. São pessoas visionárias, humanitárias e independentes.',
    strengths: ['Inovador', 'Original', 'Humanitário', 'Progressista', 'Independente', 'Intelectual'],
    weaknesses: ['Rebelde', 'Excêntrico', 'Imprevisível', 'Distante'],
    symbol: '♒'
  },
  'Peixes': {
    element: 'Água',
    quality: 'Mutável',
    ruler: 'Netuno',
    description: 'Peixes é o décimo segundo e último signo do zodíaco, representando intuição, compaixão e espiritualidade. São pessoas sensíveis, sonhadoras e altamente empáticas.',
    strengths: ['Intuitivo', 'Compassivo', 'Artístico', 'Espiritual', 'Adaptável', 'Empático'],
    weaknesses: ['Escapista', 'Iludido', 'Vitimizado', 'Indeciso'],
    symbol: '♓'
  }
};

const ZodiacSignModal = ({ isOpen, onClose, sign }) => {
  if (!sign || !zodiacDescriptions[sign]) return null;

  const info = zodiacDescriptions[sign];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {info.symbol} {sign}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">Elemento</p>
                  <p className="text-gray-700">{info.element}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">Qualidade</p>
                  <p className="text-gray-700">{info.quality}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm text-purple-600 font-medium">Regente</p>
                  <p className="text-gray-700">{info.ruler}</p>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-2">Descrição</h3>
                <p className="text-gray-600">{info.description}</p>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-2">Pontos Fortes</h3>
                <div className="flex flex-wrap gap-2">
                  {info.strengths.map((strength, i) => (
                    <span key={i} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      {strength}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-800 mb-2">Desafios</h3>
                <div className="flex flex-wrap gap-2">
                  {info.weaknesses.map((weakness, i) => (
                    <span key={i} className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm">
                      {weakness}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ZodiacSignModal;